import { useCallback, useEffect, useMemo, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'

import { PayPalScriptProvider } from '@paypal/react-paypal-js'
import { Elements } from '@stripe/react-stripe-js'
import { StripeElementsOptions } from '@stripe/stripe-js'

import { useOSDetection } from '@/hooks/detectOS/useOSDetection'

import { AppCountryTier } from '@/enums/appCountryTier'
import { PageName } from '@/enums/pageName'
import {
  CheckoutFlowVersion,
  PaymentMethod,
  PaymentMethod as PaymentMethodEnum,
  PaymentPlatform,
} from '@/enums/payments'
import { getVersionWithHyphen, PaymentScreenVersion } from '@/enums/paymentScreenVersion'
import { StripeWalletButtonTheme } from '@/enums/stripe'
import { getDefaultPaymentMethod } from '@/utils/paymentScreenUtils'
import { getDefaultPayPalOptions } from '@/utils/paypalUtils'
import { getDefaultStripeOptions } from '@/utils/stripeUtils'
import { getPath } from '@/utils/urlUtils'

import { PageTitle } from '@/components/pageTitle'
import { PaymentMethodToggle } from '@/components/paymentMethodSwitcher/paymentMethodToggler'
import { Receipt } from '@/components/receipt'
import { WalletTypeDetector } from '@/components/walletTypeDetector'

import { useAuth } from '@/auth/useAuth'
import { useSession } from '@/core/session/useSession'

import { paypalButtonMap, stripeCheckoutFormMap, walletPaymentButtonMap } from './componentMappings'
import { useProcessContactState } from './hooks/contactState/useProcessContactState'
import useTrackPageAndPaymentScreen from './hooks/trackWebEvents/useTrackWebEvents'
import { TPaymentScreenPageProps } from './types'

const paypalOptions = getDefaultPayPalOptions()

const pageName = PageName.paymentScreen

export const PaymentScreenModalV3_2: React.FunctionComponent<TPaymentScreenPageProps> = ({
  getItem,
  setItem,
  pageConfig,
  selectPlanPage,
  config,
  stripePromise,
  stripeFormAppearance,
  paypalButtonConfigStyle,
  getStripeWalletButtonStyles,
  successRedirectTo = PageName.paymentSuccess,
  contactStateRoutesMap,
}: TPaymentScreenPageProps) => {
  const { user } = useAuth()
  const { checkoutFlowVersion } = useSession()

  const navigate = useNavigate()
  const mobileOS = useOSDetection()
  const tier = getItem('appCountryTier') as AppCountryTier
  const {
    discount,
    pageTitle,
    customTitle,
    receiptData,
    receiptTitle,
    stripeFormConfig,
    paypalCustomTagline,
    loadingTitle,
  } = pageConfig

  const [walletType, setWalletType] = useState<PaymentMethodEnum | undefined>(undefined)

  const showPayPal = useMemo<boolean>(
    () => config.PAYMENT_METHODS.includes(PaymentPlatform.PAYPAL),
    [config]
  )
  const showStripe = useMemo<boolean>(
    () => config.PAYMENT_METHODS.includes(PaymentPlatform.STRIPE),
    [config]
  )

  const selectedPlanId = getItem(PageName.selectPlan)
  const planData = useMemo(
    () => selectPlanPage.plansDetails.find((plan) => plan.planId === selectedPlanId),
    [selectPlanPage.plansDetails, selectedPlanId]
  )

  const [currentPaymentMethod, setCurrentPaymentMethod] = useState<PaymentMethod | undefined>(
    PaymentMethod.CARD || PaymentMethod.PAYPAL
  )

  const handleWalletTypeChange = useCallback(
    (type: PaymentMethodEnum | undefined) => {
      setWalletType(type)
      const paymentMethod = getDefaultPaymentMethod(mobileOS, tier, type)
      setCurrentPaymentMethod(paymentMethod)
    },
    [setWalletType, setCurrentPaymentMethod, mobileOS, tier]
  )

  useEffect(() => {
    // should be run once
    if (!planData) {
      return navigate(getPath(PageName.selectPlan))
    }
  }, [navigate, planData])

  const stripeOptions = useMemo<StripeElementsOptions | undefined>(
    () => getDefaultStripeOptions(planData, stripeFormAppearance),
    [planData, stripeFormAppearance]
  )
  const paymentScreenVersion = config.PAYMENT_SCREEN_VERSION as PaymentScreenVersion
  const paymentScreenVersionWithHyphen = getVersionWithHyphen(paymentScreenVersion)
  const className = `page page-${pageName}-${paymentScreenVersionWithHyphen}`

  useTrackPageAndPaymentScreen(config, getItem, setItem, pageTitle, planData, pageName)

  const contactStateEl = useProcessContactState(
    config.PAGE_SET_ID,
    getItem,
    setItem,
    { className, stateRoutes: contactStateRoutesMap },
    checkoutFlowVersion
  )
  if (contactStateEl) {
    return contactStateEl
  }

  if (!user && checkoutFlowVersion !== CheckoutFlowVersion.V2) {
    // TODO: probably there are better typescript hints to avoid this check here
    return <Navigate to={getPath(PageName.registration)} />
  }

  if (!planData || !selectedPlanId) {
    return <Navigate to={getPath(PageName.selectPlan)} />
  }

  const changePaymentMethod = (type: PaymentMethod) => {
    setCurrentPaymentMethod(type)
  }

  if (!stripeOptions) {
    return null
  }

  const WalletPaymentFC = walletPaymentButtonMap[checkoutFlowVersion]
  const PayPalButtonFC = paypalButtonMap[checkoutFlowVersion]
  const StripeCheckoutFormFC = stripeCheckoutFormMap[checkoutFlowVersion]

  return (
    <div className={className}>
      <PayPalScriptProvider options={{ ...paypalOptions }}>
        <Elements stripe={stripePromise} options={stripeOptions}>
          <WalletTypeDetector
            getItem={getItem}
            setItem={setItem}
            onWalletTypeChange={handleWalletTypeChange}
            loadingTitle={loadingTitle}
          >
            <PageTitle title={customTitle || discount} />
            <div className="floating-container ">
              <div className="description-plan-container">
                <PaymentMethodToggle
                  onChange={changePaymentMethod}
                  currentPaymentMethod={currentPaymentMethod}
                  showPayPal={showPayPal}
                  showStripe={showStripe}
                  paymentMethodSwitcher={stripeFormConfig}
                  getItem={getItem}
                  walletType={walletType}
                  paymentScreenVersion={paymentScreenVersion}
                />
                {receiptData && selectedPlanId && (
                  <Receipt receiptData={receiptData[selectedPlanId]} receiptTitle={receiptTitle} />
                )}
              </div>

              {showStripe && currentPaymentMethod === walletType && (
                <div className="wallet-button-container">
                  <WalletPaymentFC
                    planData={planData}
                    getItem={getItem}
                    setItem={setItem}
                    pageName={pageName}
                    config={config}
                    stripeButtonTheme={StripeWalletButtonTheme.dark}
                    getStripeWalletButtonStyles={getStripeWalletButtonStyles}
                    successRedirectTo={successRedirectTo}
                    contactStateRoutesMap={contactStateRoutesMap}
                  />
                </div>
              )}

              {showPayPal && currentPaymentMethod === PaymentMethod.PAYPAL && (
                <div className="paypal-container">
                  <PayPalButtonFC
                    pageName={pageName}
                    paypalButtonConfigStyle={paypalButtonConfigStyle}
                    productData={planData}
                    getItem={getItem}
                    setItem={setItem}
                    config={config}
                    customTagline={paypalCustomTagline}
                    successRedirectTo={successRedirectTo}
                    contactStateRoutesMap={contactStateRoutesMap}
                  />
                </div>
              )}
              {showStripe && currentPaymentMethod === PaymentMethod.CARD && (
                <StripeCheckoutFormFC
                  pageName={pageName}
                  getItem={getItem}
                  setItem={setItem}
                  planDetails={planData}
                  config={config}
                  formConfig={stripeFormConfig}
                  successRedirectTo={successRedirectTo}
                  contactStateRoutesMap={contactStateRoutesMap}
                />
              )}
            </div>
          </WalletTypeDetector>
        </Elements>
      </PayPalScriptProvider>
    </div>
  )
}
